import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getBusinessConfigById, updateHomepageReviews } from '../../firebase/firebase-delegate';
import { BusinessConfig, HomepageReview } from '../../types';

const EMPTY_REVIEW: HomepageReview = { name: '', text: '', rating: 5 };
const HOMEPAGE_REVIEW_MAX_LENGTH = 150;

const normalizeReviewerName = (rawName: string): string => {
  const parts = rawName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];

  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
};

const HomepageReviewsPage: React.FC = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<HomepageReview[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const businessConfigQuery = useQuery<BusinessConfig | null>({
    queryKey: ['businessConfig', siteId],
    enabled: !!siteId,
    queryFn: () => getBusinessConfigById(siteId!),
  });

  useEffect(() => {
    if (!businessConfigQuery.data) return;
    setReviews(
      (businessConfigQuery.data.homepage.reviews ?? []).map((review) => ({
        ...review,
        rating: review.rating ?? 5,
      }))
    );
  }, [businessConfigQuery.data]);

  const saveReviewsMutation = useMutation({
    mutationFn: async () => {
      if (!siteId || !businessConfigQuery.data) {
        throw new Error('Missing site configuration.');
      }

      const sanitizedReviews = reviews
        .map((review) => ({
          name: normalizeReviewerName(review.name),
          text: review.text.trim().slice(0, HOMEPAGE_REVIEW_MAX_LENGTH),
          rating: review.rating ?? 5,
        }))
        .filter((review) => review.name.length > 0 && review.text.length > 0);

      await updateHomepageReviews(sanitizedReviews, siteId);
      return sanitizedReviews;
    },
    onSuccess: (nextReviews) => {
      setReviews(nextReviews);
      setSnackbar({ open: true, message: 'Homepage reviews saved.', severity: 'success' });
    },
    onError: (error: Error) => {
      setSnackbar({ open: true, message: error.message || 'Failed to save homepage reviews.', severity: 'error' });
    },
  });

  const isLoading = businessConfigQuery.isLoading || businessConfigQuery.isFetching;
  const isError = businessConfigQuery.isError || !businessConfigQuery.data;

  const handleChangeReview = (index: number, key: keyof HomepageReview, value: string) => {
    setReviews((prev) => prev.map((review, i) => (i === index ? { ...review, [key]: value } : review)));
  };

  const handleAddReview = () => {
    setReviews((prev) => [...prev, { ...EMPTY_REVIEW }]);
  };

  const handleRemoveReview = (index: number) => {
    setReviews((prev) => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Failed to load business config for this site.</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/admin/superadmin/manage/${siteId}`)} aria-label="Back to manage site">
          <ArrowBackRoundedIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Homepage Reviews
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add customer reviews shown on the homepage. Reviews here are always rendered as 5 stars.
      </Typography>

      <Stack spacing={2.5}>
        {reviews.map((review, index) => (
          <Card key={`review-${index}`} variant="outlined">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Review {index + 1}
                </Typography>
                <IconButton aria-label="Delete review" onClick={() => handleRemoveReview(index)}>
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              </Stack>

              <Stack spacing={2}>
                <TextField
                  label="Customer Name"
                  value={review.name}
                  onChange={(e) => handleChangeReview(index, 'name', e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Rating (out of 5)"
                  value={review.rating ?? 5}
                  fullWidth
                  disabled
                  helperText="Default is 5 for now."
                />
                <TextField
                  label="Review Text"
                  value={review.text}
                  onChange={(e) => handleChangeReview(index, 'text', e.target.value.slice(0, HOMEPAGE_REVIEW_MAX_LENGTH))}
                  fullWidth
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: HOMEPAGE_REVIEW_MAX_LENGTH }}
                  helperText={`${review.text.length}/${HOMEPAGE_REVIEW_MAX_LENGTH}`}
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button startIcon={<AddRoundedIcon />} variant="outlined" onClick={handleAddReview}>
          Add Review
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={() => saveReviewsMutation.mutate()}
          disabled={saveReviewsMutation.isPending}
        >
          {saveReviewsMutation.isPending ? 'Saving...' : 'Save Reviews'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HomepageReviewsPage;
